import React, { useEffect, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { FileText, Download, ArrowLeft } from "lucide-react";

export function PortalImg({ photoId, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API}/portal/photos/${photoId}/image`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
        if (res.ok) { revoked = URL.createObjectURL(await res.blob()); setUrl(revoked); }
      } catch (e) { console.warn("PortalImg load failed", e); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [photoId]);
  if (!url) return <div className={`bg-stone-100 animate-pulse ${className}`} />;
  return <img src={url} alt="field" className={className} />;
}

export default function PortalReports() {
  const [reports, setReports] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/portal/reports").then(({ data }) => setReports(data)); }, []);
  if (!reports) return <div className="p-6 font-bold uppercase text-sm">Loading reports…</div>;

  const open = async (r) => {
    const { data } = await api.get(`/portal/reports/${r.id}`);
    setDetail(data);
  };

  const downloadPdf = async (rep) => {
    const token = getToken();
    const res = await fetch(`${API}/portal/reports/${rep.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Field_Report_${rep.vineyard_name}_${rep.report_date}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (detail) {
    const { report, photos, completed_tasks, upcoming_tasks } = detail;
    return (
      <div className="space-y-4" data-testid="portal-report-detail">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button data-testid="portal-report-back" onClick={() => setDetail(null)} className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1"><ArrowLeft size={13} /> All Reports</button>
          <button data-testid="portal-report-pdf" onClick={() => downloadPdf(report)} className="h-10 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2"><Download size={14} /> Download PDF</button>
        </div>
        <div className="bg-white border border-stone-200 p-5 space-y-4">
          <div>
            <div className="font-bold text-xl" style={{ fontFamily: "'Fraunces', serif" }}>{report.vineyard_name} — Field Report</div>
            <div className="text-xs text-stone-500">{report.report_date} · v{report.version} · Prepared by {report.prepared_by}</div>
          </div>
          {report.overview && <Section title="Overview" body={report.overview} />}
          {report.health_summary && <Section title="Vine Health" body={report.health_summary} />}
          {(report.priorities || []).length > 0 && (
            <div>
              <div className="font-bold uppercase text-[10px] text-stone-500 mb-1">Priorities</div>
              <ul className="text-sm space-y-1">{report.priorities.map((p, i) => <li key={i}>• {p}</li>)}</ul>
            </div>
          )}
          {(report.block_rows || []).length > 0 && (
            <div className="overflow-x-auto">
              <div className="font-bold uppercase text-[10px] text-stone-500 mb-1">Block Observations</div>
              <table className="w-full text-xs">
                <thead><tr className="bg-stone-100 text-left"><th className="p-2">Block</th><th className="p-2">Stage</th><th className="p-2">Health</th><th className="p-2">Findings</th></tr></thead>
                <tbody>{report.block_rows.map((b, i) => (
                  <tr key={i} className="border-t border-stone-200">
                    <td className="p-2 font-bold">{b.block}</td><td className="p-2">{b.growth_stage || "—"}</td>
                    <td className="p-2">{b.health || "—"}</td><td className="p-2">{b.findings || b.notes || "—"}</td>
                  </tr>))}
                </tbody>
              </table>
            </div>
          )}
          {(upcoming_tasks || []).length > 0 && (
            <div>
              <div className="font-bold uppercase text-[10px] text-stone-500 mb-1">Upcoming Work</div>
              <ul className="text-sm space-y-1">{upcoming_tasks.map((t) => <li key={t.id}>→ {t.title}</li>)}</ul>
            </div>
          )}
          {(photos || []).length > 0 && (
            <div>
              <div className="font-bold uppercase text-[10px] text-stone-500 mb-2">Field Photos</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {photos.map((p) => (
                  <div key={p.id}>
                    <PortalImg photoId={p.id} className="w-full h-28 object-cover" />
                    {p.caption && <div className="text-[10px] text-stone-500 mt-0.5">{p.caption}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(completed_tasks || []).length > 0 && (
            <div>
              <div className="font-bold uppercase text-[10px] text-stone-500 mb-1">Work Completed</div>
              <ul className="text-sm space-y-1">{completed_tasks.map((t) => <li key={t.id}>✓ {t.title}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="portal-reports">
      {reports.map((r) => (
        <button key={r.id} data-testid={`portal-report-${r.id}`} onClick={() => open(r)}
          className="w-full text-left bg-white border border-stone-200 p-4 flex items-center gap-3 hover:border-[#1F3D34]">
          <FileText size={18} className="text-[#7C9673] shrink-0" />
          <div className="flex-1">
            <div className="font-bold">{r.vineyard_name} — Field Report <span className="text-stone-400 font-normal text-xs">v{r.version}</span></div>
            <div className="text-xs text-stone-500">{r.report_date} · Prepared by {r.prepared_by}</div>
          </div>
        </button>
      ))}
      {reports.length === 0 && (
        <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="portal-reports-empty">No published reports yet</div>
      )}
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div>
      <div className="font-bold uppercase text-[10px] text-stone-500 mb-1">{title}</div>
      <p className="text-sm whitespace-pre-wrap">{body}</p>
    </div>
  );
}
